import React from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowChevronArrow from './components/WorkflowChevronArrow';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'Steeringルール',
    title: 'Steeringルールの書き方を学ぶ',
    path: '/devdocs/handson/steering-rules',
  },
  { label: 'rfp作成', title: 'rfpを作成する', path: '/devdocs/handson/rfp' },
  {
    label: 'proposal/business',
    title: 'proposal/businessを生成する',
    path: '/devdocs/handson/proposal-business',
  },
  { label: 'usecase', title: 'usecaseを生成する', active: true },
  { label: 'product', title: 'productの機能一覧を生成する', path: '/devdocs/handson/product' },
];

const HandsonUsecase: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="要件定義編 — usecase生成"
      seriesName="要件定義編"
      tags={['Hands-on', 'Usecase', 'PDCA', 'System Design']}
      seriesSteps={seriesSteps}
      prevLink={{ label: 'proposal/business生成', path: '/devdocs/handson/proposal-business' }}
      nextLink={{ label: 'product作成', path: '/devdocs/handson/product' }}
    >
      <WorkflowGoalBox
        prerequisites={['「要件定義編 — proposal/business生成」が完了している']}
        completionCriteria={[
          '.kiro/steering/create_usecases.md が作成されている',
          '.kiro/requirements/usecases/todo.usecases.md が生成されている',
          'セクション4のチェックリスト全項目に合格している',
          '少なくとも1回はルール改善→再生成のサイクルを経験している',
        ]}
      >
        Steeringルールの作成・改善を繰り返しながら、AIでusecases.mdを生成し、チェックリストに合格するまでPDCAを回す。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="要件定義編 — proposal/business生成"
        description="前の記事"
        path="/devdocs/handson/proposal-business"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: 'Steeringルールを作成する' },
          { id: 'section-3', label: 'AIでusecases.mdを生成する' },
          { id: 'section-4', label: 'チェックリストで品質を確認する' },
          { id: 'section-5', label: 'ルールを改善して再生成する' },
          { id: 'section-6', label: '現在のプロジェクト構成' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowSection title="3回目のPDCAサイクル — 精度を上げる" subNumber="1.1">
          <WorkflowText>
            「要件定義編 —
            proposal/business生成」でPDCAサイクルの基本を体験しました。この章では同じサイクルを
            usecases.md に対して回します。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — proposal/business生成"
            description="PDCAサイクルの初回体験"
            path="/devdocs/handson/proposal-business"
          />

          <Grid container spacing={1} sx={{ maxWidth: 480, mx: 'auto', my: 2 }}>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Plan"
                sub="ルール作成"
                bgcolor="#1565c0"
                borderColor="#0d47a1"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Do"
                sub="AI生成"
                bgcolor="#2e7d32"
                borderColor="#1b5e20"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowUpwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={2} />
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowDownwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Act"
                sub="ルール改善"
                bgcolor="#e65100"
                borderColor="#bf360c"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon
                sx={{ fontSize: 22, color: 'text.disabled', transform: 'rotate(180deg)' }}
              />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Check"
                sub="品質確認"
                bgcolor="#f57f17"
                borderColor="#e65100"
                sx={{ height: '100%' }}
              />
            </Grid>
          </Grid>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              usecases.md は要件定義の最終成果物です。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              proposal/businessと比べて「エラーケースの網羅性」「DB操作レベルの結果記述」など、より具体的な精度が求められます。ルールの書き方も一段レベルアップする必要があります。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="この章の流れ" subNumber="1.2">
          <WorkflowList
            variant="ordered"
            items={[
              'usecases.mdの役割を確認する（紹介記事を読む）',
              'Steeringルールを作成する（create_usecases.md）',
              'AIでusecases.mdを生成する',
              'チェックリストで品質を確認する → 不合格箇所を特定',
              'ルールを改善して再生成する → 合格するまで繰り返す',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="参照する記事" subNumber="1.3">
          <WorkflowText>
            以下の紹介記事に、usecases.mdの詳細な説明・business.mdとの違い・出力手順・チェックリストが書かれています。この章を進める前に必ず読んでください。
          </WorkflowText>
          <WorkflowLinkCard
            title="AIと作る、壊れないバックエンド紹介記事 — システムユースケースを出力する"
            description="usecases.mdの構造・business.mdとの違い・出力手順・提出前チェックリスト"
            path="/devdocs/workflow/req-step4"
          />

          <WorkflowTipBox title="なぜ紹介記事を先に読むのか">
            <WorkflowText>
              「要件定義編 —
              proposal/business生成」と同じ理由です。usecases.mdの意味・なぜbusiness.mdと分けるのか・どういう精度が求められるのか等の知識面は紹介記事に詳しく書かれています。知識がないままルールを書いても「何を書けばいいか分からない」状態になります。
            </WorkflowText>
            <WorkflowText>特に以下のセクションは重要です：</WorkflowText>
            <WorkflowList
              items={[
                'Step4 セクション1「概要」— usecases.mdの位置づけ、なぜbusiness.mdだけでは不十分か',
                'Step4 セクション2「成果物」— テンプレート構造・記載ルール',
                'Step4 セクション3「business.mdとの違い」— 具体的な変換例',
                'Step4 セクション8「提出前チェックリスト」— この章のゴール基準',
              ]}
            />
          </WorkflowTipBox>

          <WorkflowTipBox title="usecases.mdルールのポイント — proposal/businessルールとの違い">
            <WorkflowTable
              headers={['観点', 'create_proposal / create_business', 'create_usecases']}
              rows={[
                ['入力ファイル', 'rfp.md（1ファイル）', 'proposal.md + business.md（2ファイル）'],
                [
                  '対話の有無',
                  '対話を通じて仕様を決定する',
                  '対話なし。既に確定した仕様を変換する',
                ],
                [
                  'ルールの主な役割',
                  '「何を聞くか」「何を決めるか」の指示',
                  '「どう変換するか」「何を網羅するか」の指示',
                ],
                ['品質のボトルネック', '確認事項の網羅性', 'エラーケースの網羅性・結果の具体性'],
              ]}
            />
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="Steeringルールを作成する" id="section-2" number={2}>
        <WorkflowSection title="何を作るか" subNumber="2.1">
          <WorkflowTable
            headers={['ファイル', '役割']}
            rows={[
              [
                '.kiro/steering/create_usecases.md',
                'usecases.md の生成ルール（テンプレート・記載ルール・チェックリスト）',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="ルールに書くべき情報" subNumber="2.2">
          <WorkflowText>
            「要件定義編 —
            proposal/business生成」と同じ共通構造を使います。ただし、usecases.mdはproposal +
            businessの両方を入力とする点で、前2つのルールと異なります。
          </WorkflowText>
          <WorkflowTable
            headers={['順番', 'セクション', '書くこと']}
            rows={[
              ['1', '目的', 'このルールが何のために存在するか'],
              ['2', '成果物説明', '生成するファイルが何であるか'],
              ['3', '配置先', 'ファイルをどこに置くか'],
              ['4', '生成フロー', 'どういう手順で生成するか（入力が2ファイルある点に注意）'],
              ['5', '確認事項', '生成前にAIが確認すべきこと'],
              ['6', 'テンプレート', '出力フォーマット'],
              ['7', '記載ルール', 'テンプレートの各項目をどう埋めるか'],
              ['8', 'チェックリスト', '生成後の品質確認項目'],
              ['9', '禁止事項', 'AIがやってはいけないこと'],
            ]}
          />
          <WorkflowTipBox title="読み込みモードの設定">
            <WorkflowText>
              このファイルは fileMatch モードで、proposal.md / business.md / usecases.md
              を開いたときに自動で読み込まれるようにします。
            </WorkflowText>
            <WorkflowCodeBlock
              language="yaml"
              code={`---
inclusion: fileMatch
globs:
  - ".kiro/requirements/**/*.proposal.md"
  - ".kiro/requirements/usecases/*.business.md"
  - ".kiro/requirements/usecases/*.usecases.md"
---`}
            />
            <WorkflowText>fileMatchPattern は非推奨です。globs を使ってください。</WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="2.3">
          <WorkflowText>以下のファイルを作成してください。</WorkflowText>
          <WorkflowList items={['.kiro/steering/create_usecases.md']} />
          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              紹介記事の例はKanzhong（中・大規模業務システム）向けです。TODOアプリ向けにはもっとシンプルで構いません。以下の点を意識してください：
            </Typography>
            <WorkflowList
              items={[
                '認証なし → エラーケースの401/403は不要。ただし「認証がある場合は401/403を全UCに含める」と条件付きで書いておくと汎用的',
                'テーブルが少ない → アクター定義セクションは「全ユーザー」1行で十分',
                'master_typeを使わない場合 → 区分定義セクションは省略可',
                'ビジネスルールがシンプル → ステータス遷移表が不要なら省略可',
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="create_usecases.md - 完成例" subNumber="2.4">
          <WorkflowLockedCodeBlock password="Kp7nWf">
            {`---
inclusion: fileMatch
globs:
  - ".kiro/requirements/**/*.proposal.md"
  - ".kiro/requirements/usecases/*.business.md"
  - ".kiro/requirements/usecases/*.usecases.md"
---

# usecases.md 生成ルール

## 目的

proposal.md（技術仕様）と business.md（ユーザー視点シナリオ）を元に、
API単位で技術的に再定義した \`*.usecases.md\`（システムユースケース）を生成するためのルール。

## 成果物説明

- usecases.md は business.md のユーザー視点シナリオを「システムがどう動くか」に翻訳した文書。
- 1UC = 1API を基本とした粒度で、DB操作・エラーケースを技術的に定義する。
- 結合試験のテストケース設計のベース。
- バックエンド開発の直接インプット。
- 要件定義フェーズの最終成果物。

## 配置先

| 項目 | 内容 |
|------|------|
| 配置先 | \`.kiro/requirements/usecases/\` |
| ファイル名 | \`{機能名}.usecases.md\` |

## 生成フロー

- proposal.md と business.md が完成していることが前提。
- 対話不要。既に確定した仕様の「変換」。
- 不明点がある場合は生成を止めてユーザーに確認する。

### 生成の流れ

1. proposal.md のAPI一覧から全UCを洗い出す（1API = 1UC が基本）
2. business.md の各BUCと対応するUCを紐付ける
3. business.md の手順をAPIフィールド名に変換して記述する
4. proposal.md のテーブル定義からDB操作レベルの結果を記述する
5. 全APIのエラーケースを網羅する
6. ビジネスルールセクションを proposal.md から転記・整理する

## 記載ルール

### 手順
- business.md の手順をベースに、APIフィールド名で記述する。
- フィールド名の後に括弧で日本語名を付記する。例: title（タイトル）

### 結果
- DB操作レベルで具体的に記述する（テーブル名 + INSERT/UPDATE/DELETE）。
- 自動設定される項目（created_at, UUID等）を明記する。

### エラーケース
- 全エラーパターンをHTTPステータスコード付きで網羅する。
- バリデーションエラー（400）はproposal.mdのテーブル定義の制約から導出する。
- 認証がない場合は401/403を省略する。

### ビジネスルール
- proposal.md のビジネスルールをシステム視点で整理して転記する。
- 独自に追加・改変しない。

## チェックリスト

- [ ] proposal.md の全APIに対して1つ以上のUCが存在するか
- [ ] 全UCのエラーケースにバリデーションエラー（400）が含まれているか
- [ ] 結果がDB操作レベル（テーブル名+操作種別）で記述されているか
- [ ] 手順がAPIフィールド名で記述されているか
- [ ] business.md の全BUCが少なくとも1つのUCと対応しているか
- [ ] ビジネスルールが proposal.md と整合しているか

## 禁止事項

- proposal.md に存在しないAPIのUCを作成しない。
- エラーケースを省略しない。
- 結果を曖昧に書かない（「登録される」ではなく「{テーブル名}にINSERTされる」）。
- ビジネスルールを独自に追加しない。
- 手順を画面ラベル名で記述しない（APIフィールド名を使う）。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="AIでusecases.mdを生成する" id="section-3" number={3}>
        <WorkflowSection title="生成を指示する" subNumber="3.1">
          <WorkflowText>
            proposal.md と business.md
            をコンテキストに含めて、usecases.mdの生成を指示してください。指示方法（一括生成/段階的生成）のプロンプト例は紹介記事の「出力手順」セクションを参照してください。
          </WorkflowText>
          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              usecases.md は「対話」ではなく「変換」です。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              proposal/businessの生成時はAIと対話して仕様を決めましたが、usecases.md
              では既に確定した仕様を技術的に翻訳するだけです。生成指示を出せば一発で出力されます。
            </Typography>
          </WorkflowInfoBox>
          <WorkflowLinkCard
            title="AIと作る、壊れないバックエンド紹介記事 — システムユースケースを出力する"
            description="一括生成/段階的生成のプロンプト例・修正指示の出し方"
            path="/devdocs/workflow/req-step4"
          />
        </WorkflowSection>

        <WorkflowSection title="生成後に整合性チェックを依頼する" subNumber="3.2">
          <WorkflowText>
            生成が完了したら、AIに整合性チェックを依頼します。プロンプト例は紹介記事のセクション4「生成後の確認」を参照してください。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="チェックリストで品質を確認する" id="section-4" number={4}>
        <WorkflowSection title="チェックの基準" subNumber="4.1">
          <WorkflowText>
            品質確認には紹介記事のセクション8「提出前チェックリスト」を使います。
          </WorkflowText>
          <WorkflowLinkCard
            title="AIと作る、壊れないバックエンド紹介記事 — システムユースケースを出力する"
            description="セクション8「提出前チェックリスト」— 網羅性・記述レベル・整合性の確認項目"
            path="/devdocs/workflow/req-step4"
          />
        </WorkflowSection>

        <WorkflowSection title="不合格パターンと改善ケーススタディ" subNumber="4.2">
          <WorkflowText>
            チェックリストで不合格が出たら、原因を切り分けて対処方針を決めます。
          </WorkflowText>
          <WorkflowTable
            headers={['分類', '対処']}
            rows={[
              ['[ルール不足] ルールの記載が甘くてAIが間違える', 'セクション5でルールを改善する'],
              ['[部分ミス] 生成物の一部が間違っている', 'AIに修正指示を出す'],
            ]}
          />
          <WorkflowInfoBox type="info">
            <Typography variant="body2">
              usecases.md
              は対話なしの変換なので「対話不足」パターンは基本的に発生しません。不合格の原因は「ルール不足」か「部分ミス」のどちらかです。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowText>
            以下は [ルール不足]
            に該当するケーススタディです。チェック不合格の症状から、ルールのどこを直すべきかを逆引きできます。
          </WorkflowText>

          <WorkflowTable
            headers={['#', '症状', '原因', '改善']}
            rows={[
              [
                '1',
                '結果が「TODOが登録される」で終わっている',
                '自動設定項目の例示がない',
                '記載ルールに具体例を追記',
              ],
              [
                '2',
                'エラーケースが400の1行しかない',
                '400の内訳の粒度が不明確',
                'テーブル定義の制約から全パターン導出と追記',
              ],
              [
                '3',
                '手順が画面ラベル名のまま',
                '変換の具体例がない',
                'フィールド名変換の例示を追記',
              ],
              [
                '4',
                '独自ビジネスルールが追加されている',
                'AI がAPI概要の備考をルール化',
                '禁止事項に明確化を追記',
              ],
              [
                '5',
                'UC-IDの連番が飛んでいる',
                '削除されたUCの番号が残った',
                'チェックリストに連番確認を追加',
              ],
              [
                '6',
                'proposal.mdのAPI数よりUCが少ない',
                '一括生成で後半が省略',
                '段階的生成に切り替えるか不足追加指示',
              ],
            ]}
          />

          <WorkflowTipBox title="ケーススタディの使い方">
            <WorkflowText>チェックリストで不合格が出たら：</WorkflowText>
            <WorkflowList
              variant="ordered"
              items={[
                '上の表から似た症状を探す',
                '「原因」列を読んで、自分のルールの同じ箇所を確認する',
                '「改善」列を参考に、自分のSteeringファイルを修正する',
                '再生成してチェックを通す',
              ]}
            />
            <WorkflowText>
              ここに載っていないパターンが出た場合は、同じ思考プロセス（症状→原因→改善）で自分で分析してください。「要件定義編
              —
              proposal/business生成」から3回目のPDCAサイクルですから、もうこの思考プロセスが身についているはずです。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="ルールを改善して再生成する" id="section-5" number={5}>
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600}>
            重要: このセクションは完了条件の1つです。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            1回目の生成でチェックリストに全合格しても、意図的にルールを1箇所削って再生成し、品質が下がることを確認してください。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowSection title="改善の手順" subNumber="5.1">
          <WorkflowText>
            「要件定義編 —
            proposal/business生成」のセクション5と同じ手順（原因特定→修正箇所決定→Steering修正→再生成→再チェック）を繰り返してください。合格するまでサイクルを回します。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — proposal/business生成"
            description="ルール改善の手順"
            path="/devdocs/handson/proposal-business"
          />

          <WorkflowChatBubble role="human">
            {`create_usecases.md のルールを更新しました。
更新したルールに従って、todo.usecases.md を再生成してください。`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: usecases.md
              は要件定義の最終成果物です。ここのチェックが甘いと、バックエンド開発以降で「仕様が曖昧で実装できない」「テストケースが書けない」問題が発生します。チェックリストは形式的に流さず、1項目ずつ確認してください。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-6" number={6}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'requirements',
                  children: [
                    {
                      name: 'todo',
                      children: [{ name: 'todo.rfp.md' }, { name: 'todo.proposal.md' }],
                    },
                    {
                      name: 'usecases',
                      children: [
                        { name: 'todo.business.md' },
                        { name: 'todo.usecases.md', desc: '今回生成', highlight: '#1976d2' },
                      ],
                    },
                  ],
                },
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'create_proposal.md' },
                    { name: 'create_business.md' },
                    { name: 'create_usecases.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: 'HELPME.md' },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        { name: 'config', children: [{ name: 'SecurityConfig.java' }] },
                      ],
                    },
                    { name: 'resources', children: [{ name: 'application.yml' }] },
                  ],
                },
                { name: 'test', children: [] },
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* 次のステップ */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          要件定義編 — product作成
        </Typography>
        <WorkflowList
          items={[
            'productルール（create_product.md）を作成する',
            'usecases.md までの全成果物を統合してproduct.mdを生成する',
            '要件定義フェーズの総まとめ',
          ]}
        />
      </WorkflowInfoBox>

      <WorkflowLinkCard
        title="要件定義編 — product作成"
        description="usecases.md までの全成果物を統合してproduct.mdを生成し、要件定義フェーズを完了する"
        path="/devdocs/handson/product"
      />

      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              '.kiro/steering/create_usecases.md が作成されている',
              '.kiro/requirements/usecases/todo.usecases.md が生成されている',
              'セクション4のチェックリスト全項目に合格している',
              '少なくとも1回はルール改善→再生成のサイクルを経験した',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonUsecase;
